import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { validateUserFilesUploadFile } from '@/user_files/utils/upload-validation'
import { isTerminalProcessingStatus } from '@/user_files/utils/processing-status'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { kUppyUploadMaxFileSizeBytes } from '@/uppy-file-upload/constants'
import { getOrCreateUserFilesUploadFolderId } from '@/user_files/folders/utils/get-or-create-user-files-upload-folder-id'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { MED_CHRON_FOLDER_NAME } from '@/medical-chronology/jobs/constants'
import { kMedChronAssetAcceptedFileTypes } from '../constants'
import { MedChronAssetActions } from '../store/slice'

/**
 * Direct-upload bridge for the Add Files page: PDF validation, dropzone/picker wiring,
 * upload-folder management, and polling registration for staged files still processing.
 * The pending→staged conversion is a store listener, not a hook callback (see listeners.ts).
 *
 * Clone of useMedChronUserFilesUpload rewired to the asset Add Files staging state.
 */
type UseMedChronAssetFilesUploadParams = {
  enabled: boolean
  matterId: string | null
}

function isMedChronAssetUploadFileAccepted(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

export function useMedChronAssetFilesUpload({ enabled, matterId }: UseMedChronAssetFilesUploadParams) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const stagedUserFileIds = useAppSelector((state: RootState) => state.medChronAssetState.addFilesStaging.stagedUserFileIds)
  const pendingUploadClientIds = useAppSelector((state: RootState) => state.medChronAssetState.addFilesStaging.pendingUploadClientIds)
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null)
  const uploadFolderIdRef = useRef<string | null>(null)
  const currentMatterIdRef = useRef<string | null>(matterId)
  const filesWaitingForUploadFolderRef = useRef<File[] | null>(null)

  const fileIdsNeedingPolling = useMemo(
    () =>
      stagedUserFileIds.filter((fileId) => {
        const uploadFile = uploadFilesByServerFileId[fileId]
        if (!uploadFile) return false
        if (uploadFile.client.uploadStatus !== 'uploaded') return false
        return !isTerminalProcessingStatus(uploadFile.backend?.record.processing_status)
      }),
    [stagedUserFileIds, uploadFilesByServerFileId]
  )

  useUserFilesPollingProcessor({
    sourceId: `med-chron-asset-files-upload-${matterId ?? 'none'}`,
    fileIds: fileIdsNeedingPolling,
    enabled: enabled && fileIdsNeedingPolling.length > 0,
  })

  const {
    openUploadPicker,
    disabled: driveUploadDisabled,
    fileInputRef,
    addFiles,
    cancelUpload,
    conflictDialog,
  } = useDriveUpload({
    folderId: enabled ? uploadFolderId : null,
    isFileAccepted: isMedChronAssetUploadFileAccepted,
    validateUploadFile: validateUserFilesUploadFile,
    onFileRegistered: ({ clientTmpId }) => {
      dispatch(MedChronAssetActions.addPendingUserFileUpload(clientTmpId))
    },
    // pending→staged conversion is NOT wired here: it lives in the store listener keyed on
    // UppyFileUploadActions.fileInitialized, so uploads adopted by the MatterLayout fallback
    // after this page unmounts still get staged (see medical-chronology/asset/store/listeners.ts).
    onUploadCompleted: ({ folderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
    },
    onUploadFailed: ({ folderId }) => {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
    },
  })

  const disabled = !enabled || (Boolean(uploadFolderId) && driveUploadDisabled)

  const prepareUploadFolder = useCallback(async (): Promise<string | null> => {
    if (!enabled) return null
    if (uploadFolderIdRef.current) return uploadFolderIdRef.current

    const requestedMatterId = matterId
    try {
      const folderId = await getOrCreateUserFilesUploadFolderId({
        dispatch,
        folderName: MED_CHRON_FOLDER_NAME,
        getState: store.getState,
        matterId: requestedMatterId,
      })
      if (currentMatterIdRef.current !== requestedMatterId) return null

      uploadFolderIdRef.current = folderId
      setUploadFolderId(folderId)
      return folderId
    } catch (error) {
      console.error('Failed to prepare Matter Files upload folder:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'File could not be uploaded.',
          durationMs: kToastDurationMediumMs,
        })
      )
      return null
    }
  }, [dispatch, enabled, matterId, store])

  // Reset the cached upload destination when the active matter changes.
  useEffect(() => {
    currentMatterIdRef.current = matterId
    uploadFolderIdRef.current = null
    filesWaitingForUploadFolderRef.current = null
    setUploadFolderId(null)
  }, [matterId])

  // Hand files queued during folder creation to useDriveUpload once the folder id
  // has reached it through props (addFiles registers against the folderId prop).
  useEffect(() => {
    if (!uploadFolderId) return

    const files = filesWaitingForUploadFolderRef.current
    if (!files) return
    filesWaitingForUploadFolderRef.current = null
    addFiles(files)
  }, [addFiles, uploadFolderId])

  const addFilesAfterUploadFolderReady = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || disabled) return

      const folderId = await prepareUploadFolder()
      if (!folderId) return

      if (uploadFolderId === folderId) {
        addFiles(files)
        return
      }

      filesWaitingForUploadFolderRef.current = [...(filesWaitingForUploadFolderRef.current ?? []), ...files]
    },
    [addFiles, disabled, prepareUploadFolder, uploadFolderId]
  )

  const addDroppedFiles = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (disabled) return
      const rejectedFiles = fileRejections.map((fileRejection) => fileRejection.file)
      void addFilesAfterUploadFolderReady([...acceptedFiles, ...rejectedFiles])
    },
    [addFilesAfterUploadFolderReady, disabled]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addDroppedFiles,
    accept: kMedChronAssetAcceptedFileTypes,
    noClick: true,
    maxSize: kUppyUploadMaxFileSizeBytes,
    disabled,
  })

  const handleFilesPicked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      const files = event.target.files ? Array.from(event.target.files) : []
      event.target.value = ''
      void addFilesAfterUploadFolderReady(files)
    },
    [addFilesAfterUploadFolderReady, disabled]
  )

  // Make sure the upload folder exists, then open the OS picker by clicking the hidden
  // input directly. The input is mounted regardless of folder state, and the picked files
  // read the folder from a later render, so there is nothing to wait for. Deferring the
  // open through a state->effect handoff (the previous shape) dropped the click when the
  // folder resolved from the warm store in the same task — the first picker open after
  // returning to the Add Files form never happened.
  const open = useCallback(() => {
    if (disabled) return
    void (async () => {
      const folderId = await prepareUploadFolder()
      if (!folderId) return

      if (uploadFolderId === folderId) {
        openUploadPicker()
        return
      }

      fileInputRef.current?.click()
    })()
  }, [disabled, fileInputRef, openUploadPicker, prepareUploadFolder, uploadFolderId])

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    fileInputRef,
    onFilesPicked: handleFilesPicked,
    cancelUpload,
    conflictDialog,
    disabled,
    pendingUploadClientIds,
  }
}
